import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from '@/lib/i18n';
import { RadioGroup } from '@/components/ui';
import type { ProductFormValues } from '../schemas/productSchema';
import { OptionGroupsEditor } from './OptionGroupsEditor';

/**
 * 옵션 방식 — UI 파생 상태. 'none'=옵션이 없는 상품(QA-521, 기본 SKU 1개의 재고만), 'single'=평면 옵션, 'combo'=조합(그룹) 옵션.
 */
export type OptionMode = 'none' | 'single' | 'combo';

export interface OptionConfigBlockProps {
  control: Control<ProductFormValues>;
  /** 현재 옵션 방식(UI 파생). */
  mode: OptionMode;
  /** 방식 토글. 'single'→'combo' 전환 시 그룹 편집기가 열린다. */
  onModeChange: (mode: OptionMode) => void;
  /** 수정 모드 여부 — onChange 의 빈 배열 처리 분기(수정 [] = 그룹 전체 제거, 등록 [] = 미사용). */
  isEditMode: boolean;
  disabled?: boolean;
}

/**
 * §4 상단 "옵션 구성" 블록 — 옵션 방식 토글(단일/조합) + 조합 모드일 때 그룹 생성기(OptionGroupsEditor).
 *
 * 방식은 **UI 파생 상태**다(스키마 신규 필드 없음): 'combo' 선택 시 그룹 편집기가 노출되고, 거기에 그룹을
 * 1개 이상 채우면 optionGroups 가 활성화돼 BE 가 데카르트 곱으로 조합 variant 를 생성한다(혼용 금지
 * 게이트는 ProductForm 의 optionGroupsActive 가 계속 소유). 'single' 은 그룹 편집기를 숨긴다.
 *
 * 그룹 생성·선택지·추가금·조합 상한(100) 안내는 OptionGroupsEditor 가 그대로 담당(계약/로직 불변).
 */
export const OptionConfigBlock = ({
  control,
  mode,
  onModeChange,
  isEditMode,
  disabled = false,
}: OptionConfigBlockProps) => {
  const { t } = useTranslation('products');

  return (
    <div className="flex flex-col gap-4" data-testid="option-config-block">
      <RadioGroup
        value={mode}
        onValueChange={(v) => onModeChange(v as OptionMode)}
        orientation="horizontal"
        aria-label={t('products.optionGroups.modeLabel', '옵션 방식')}
        {...(disabled ? { disabled: true } : {})}
        data-testid="option-mode-toggle"
        options={[
          {
            value: 'none',
            label: t('products.optionGroups.modeNone', '옵션이 없는 상품'),
            description: t('products.optionGroups.modeNoneDesc', '현재고·안전재고·무게·재고 추적만 입력합니다.'),
          },
          {
            value: 'single',
            label: t('products.optionGroups.modeSingle', '단일 옵션'),
            description: t('products.optionGroups.modeSingleDesc', '옵션 하나하나를 직접 입력합니다.'),
          },
          {
            value: 'combo',
            label: t('products.optionGroups.modeCombo', '조합 옵션 (그룹)'),
            description: t(
              'products.optionGroups.modeComboDesc',
              '그룹(예: 색상 × 사이즈) 조합으로 옵션을 자동 생성합니다.',
            ),
          },
        ]}
      />

      {mode === 'combo' ? (
        <div className="border-t pt-4" style={{ borderColor: 'var(--divider-soft)' }}>
          {isEditMode ? (
            <p className="mb-2 text-[12px]" style={{ color: 'var(--content-muted)' }}>
              {t(
                'products.optionGroups.editHint',
                '그대로 둔 조합의 재고는 유지되고, 이름/라벨 변경도 재고가 보존됩니다. 추가한 조합은 새 옵션으로 생성되고, 삭제한 조합은 비활성화됩니다(주문 이력 보존).',
              )}
            </p>
          ) : null}
          <Controller
            control={control}
            name="optionGroups"
            render={({ field, fieldState }) => (
              <>
                <OptionGroupsEditor
                  value={field.value ?? []}
                  onChange={(next) =>
                    // 등록: 빈 배열 → undefined(미사용, 페이로드 생략).
                    // 수정: 빈 배열 그대로 유지 — wire [] = "그룹 전체 제거"(BE reconcile),
                    //       undefined = "미변경" 으로 의미가 달라 구분해야 한다.
                    field.onChange(isEditMode ? next : next.length > 0 ? next : undefined)
                  }
                  disabled={disabled}
                />
                {fieldState.error?.message ? (
                  <p
                    className="mt-1 text-sm font-medium"
                    style={{ color: 'var(--status-danger-fg)' }}
                  >
                    {t(fieldState.error.message, { defaultValue: fieldState.error.message })}
                  </p>
                ) : null}
              </>
            )}
          />
        </div>
      ) : null}
    </div>
  );
};
